import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { IconButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, MouseEvent } from "react";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";

type Props = {
	/** Human-readable reason shown on hover/focus. */
	tooltip: string;
	onClick: (event: MouseEvent<HTMLButtonElement>) => void;
	/** Accessible label for the icon-only button (defaults to the tooltip text). */
	ariaLabel?: string;
	/** Status severity — drives the fill color from the theme palette. */
	severity?: "error" | "warning";
	/** Diameter of the circular badge, in px. */
	size?: number;
	dataTestId?: string;
};

/**
 * A small circular "!" attention badge that opens something on click (a
 * decision modal, a detail panel, …). Themed from the status palette — never a
 * hardcoded color. Generic: any flow that needs an inline "this row/item needs
 * your attention" affordance can reuse it.
 */
export const AlertActionBadge: FC<Props> = ({ tooltip, onClick, ariaLabel, severity = "error", size = 22, dataTestId }) => {
	return (
		<InHouseTooltip title={tooltip} arrow>
			<IconButton
				data-testid={dataTestId}
				aria-label={ariaLabel ?? tooltip}
				onClick={(event) => {
					event.stopPropagation();
					onClick(event);
				}}
				sx={(theme) => ({
					width: size,
					height: size,
					p: 0,
					color: theme.palette[severity].contrastText,
					backgroundColor: theme.palette[severity].main,
					transition: "transform 140ms cubic-bezier(0.23, 1, 0.32, 1), background-color 140ms ease",
					"&:hover": { backgroundColor: theme.palette[severity].dark },
					"&:active": { transform: "scale(0.92)" },
					"@media (prefers-reduced-motion: reduce)": { transition: "background-color 140ms ease", "&:active": { transform: "none" } },
					"&:focus-visible": { outline: `2px solid ${alpha(theme.palette[severity].main, 0.5)}`, outlineOffset: 2 },
				})}
			>
				<PriorityHighIcon sx={{ fontSize: size * 0.62 }} />
			</IconButton>
		</InHouseTooltip>
	);
};
